import { GetStaticProps } from "next";
import {
    StoryblokComponent,
    getStoryblokApi,
    useStoryblokState
} from "@storyblok/react";
import Head from "next/head";

import Filter from "components/faq/filter/Filter";
import Layout from "components/layout/Layout";
import PageHero from "components/shared/pageHero/PageHero";

import type { FaqCategoryDatasource, FaqPageStory } from "types/storyblok";

function FaqPage({
    story: initialStory,
    categories
}: {
    story: FaqPageStory;
    categories: FaqCategoryDatasource;
}): JSX.Element {
    const story = useStoryblokState(initialStory);
    const seo = story.content.seo;
    return (
        <>
            <Head>
                <title>{seo.title}</title>
                <meta name="description" content={seo?.description ?? ""} />
            </Head>
            <Layout>
                <PageHero title={story?.content?.pageTitle ?? ""} />
                <Filter
                    filters={categories.datasource_entries.map(category => ({
                        name: category.name,
                        value: category.value.toLowerCase()
                    }))}
                />
                <StoryblokComponent blok={story.content} />
            </Layout>
        </>
    );
}

export const getStaticProps: GetStaticProps = async () => {
    // home is the default slug for the homepage in Storyblok
    const slug = "faq";

    const sbParams = {
        resolve_relations: "category",
        version: process.env.NODE_ENV === "production" ? "published" : "draft"
    };

    const storyblokApi = getStoryblokApi();
    const { data: page } = await storyblokApi.get(
        `cdn/stories/${slug}?cv=${Date.now()}`,
        sbParams
    );
    const { data: categories } = await storyblokApi.get(
        `cdn/datasource_entries?cv=${Date.now()}`,
        {
            datasource: "faqcategories"
        }
    );

    return {
        props: {
            categories: categories ? categories : false,
            key: page ? page.story.id : false,
            story: page ? page.story : false
        },
        revalidate: 3600 // revalidate every hour
    };
};

export default FaqPage;
